import React from "react";
import TerminalRow, {
  TerminalRecord,
} from "components/terminal-row/TerminalRow";
import TerminalRowSkeleton from "components/terminal-row/TerminalRowSkeleton";
import Button from "components/button/Button";
import SearchInput from "components/search-input/SearchInput";
import { useSearch } from "components/search-input/useSearch";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { useTerminalLookup } from "features/terminals/useTerminalLookup";

// merchantId too: a resolved name replaces the id in merchantName, which is what it used to match.
const TERMINAL_SEARCH_KEYS: (keyof TerminalRecord)[] = [
  "serial",
  "merchantName",
  "merchantId",
];
// The on-chain lookup resolves one specific terminal, so it needs the whole serial — below
// this the operator is still mid-type and the lookup would only ever answer "not found".
const MIN_SERIAL_LENGTH = 12;
const isFullSerial = (value: string) => value.length >= MIN_SERIAL_LENGTH;

interface Props {
  terminals: TerminalRecord[];
  loading: boolean;
}

function TerminalsList({ terminals, loading }: Props) {
  console.log(`TerminalsList terminals:`, terminals);
  const { query, setQuery, results } = useSearch(
    terminals,
    TERMINAL_SEARCH_KEYS,
  );

  const trimmedQuery = query.trim();

  // Only the recent terminals are loaded up front; a search miss here is our cue to
  // check the chain — but only once a full serial has been entered.
  const noLocalMatch =
    !loading && trimmedQuery.length > 0 && results.length === 0;

  // Hold the network lookup until typing settles, so we don't fire a request
  // (and flash "not found") on every keystroke.
  const debouncedQuery = useDebouncedValue(trimmedQuery, 400);
  const settling = debouncedQuery !== trimmedQuery;

  const lookup = useTerminalLookup(
    debouncedQuery,
    noLocalMatch && isFullSerial(trimmedQuery) && !settling,
  );

  // While the debounce catches up we're still effectively searching; once it
  // settles, the hook's status drives the outcome (loading/found/miss/error).
  const chainStatus = settling ? "loading" : lookup.status;

  return (
    <>
      <div className="ip-section-header">
        <h3 className="ip-section-title">Terminals</h3>
        <div className="ip-section-actions">
          <SearchInput
            value={query}
            onChange={setQuery}
            placeholder="Search by serial or merchant..."
          />
        </div>
      </div>

      <div className="ip-terminals-list">
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <TerminalRowSkeleton key={i} />
          ))
        ) : results.length > 0 ? (
          results.map((t) => <TerminalRow key={t.serialId} {...t} />)
        ) : trimmedQuery.length === 0 ? (
          <p className="ip-empty">No terminals yet.</p>
        ) : !isFullSerial(trimmedQuery) ? (
          <p className="ip-empty">
            No matching terminal. Enter the full serial to look it up
            on-chain.
          </p>
        ) : chainStatus === "loading" ? (
          <TerminalRowSkeleton />
        ) : chainStatus === "found" && lookup.terminal ? (
          <>
            <p className="ip-lookup-note">
              Not in recent terminals — found on-chain.
            </p>
            <TerminalRow {...lookup.terminal} />
          </>
        ) : chainStatus === "not-found" ? (
          <p className="ip-empty">No terminal found for “{trimmedQuery}”.</p>
        ) : chainStatus === "error" ? (
          <div className="ip-lookup-error">
            <p className="ip-lookup-error__msg">
              Couldn’t look up this terminal right now.
            </p>
            <Button variant="ghost" size="sm" onClick={() => lookup.refetch()}>
              Retry
            </Button>
          </div>
        ) : (
          // `idle` — the gate closed under us (e.g. auth dropped). Say nothing rather
          // than claim a lookup failed that never ran.
          <p className="ip-empty">No terminals match your search.</p>
        )}
      </div>
    </>
  );
}

export default TerminalsList;
